<template>
  <!-- 「지원 계정으로 접속」 창 하나 — 회사 카드·목록 행·위쪽 검색이 같이 씀(사용자 결정 10-29).
       z-index 1990 = 부트스트랩 창(1055) 위, 본인 확인 창(StepUpModal 2000) 아래 -->
  <a-modal :open="open" :footer="null" :z-index="1990" :mask-closable="false" wrap-class-name="support-dialog" @cancel="close">
    <template #title>
      <div class="modal-title">지원 계정으로 접속 — {{ companyName || "이 회사" }}</div>
    </template>
    <div class="modal-content">
      <template v-if="step === 'form'">
        <template v-if="apps.length > 1">
          <label class="field-label">들어갈 앱</label>
          <a-radio-group v-model:value="entry.app">
            <a-radio v-for="a in apps" :key="a" :value="a">{{ APP_LABEL[a] }}</a-radio>
          </a-radio-group>
        </template>
        <p v-else-if="apps.length" class="field-hint">들어갈 앱: {{ APP_LABEL[apps[0]] }}</p>
        <label class="field-label">사유</label>
        <a-select v-model:value="entry.reasonCode" :options="SUPPORT_REASONS" style="width: 100%" :get-popup-container="(t) => t.parentNode" />
        <label class="field-label">메모{{ entry.reasonCode === "OTHER" ? "(필수)" : "" }}</label>
        <a-input v-model:value="entry.reasonNote" :maxlength="200" placeholder="문의 번호 등" @press-enter="start(false)" />
        <p class="field-hint">{{ ENTRY_HELP }}</p>
      </template>
      <template v-else-if="step === 'proof'">
        <p class="field-hint">{{ PROOF_HINT }}</p>
        <otp-step
          v-if="proofFactors.methods.length"
          :methods="proofFactors.methods"
          :targets="proofFactors.targets"
          :send="proofSend"
          :verify="proofVerify"
          submit-text="확인하고 접속"
        />
      </template>
      <template v-else-if="step === 'opened'">
        <p>새 탭에서 지원 접속을 열었습니다.</p>
        <p v-if="entryUrl" class="field-hint">
          새 탭이 열리지 않았으면 <a :href="entryUrl" target="_blank" rel="noopener noreferrer">새 탭 열기</a>(코드는 60초만 유효합니다).
        </p>
      </template>
      <p v-if="message" class="entry-error" role="alert">{{ message }}</p>
      <div class="modal-footer">
        <a-button @click="close">{{ step === "opened" ? "닫기" : "취소" }}</a-button>
        <a-button v-if="step === 'form'" type="primary" class="yellow-button" :loading="busy" @click="start(false)">접속</a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { errorMessage, factorsOf, getMfaToken, isStepUpCancelled, setMfaToken } from "@/utils/mfa";
import { standardErrorNotice } from "@/utils/standardErrors";
import {
  APP_LABEL,
  ENTRY_HELP,
  PROOF_HINT,
  PROOF_NEEDS_FACTOR_TEXT,
  PROOF_PURPOSE,
  SUPPORT_REASONS,
  appUrlEnv,
  appsForCompanyType,
  entryError,
  entryNextStep,
  proofBody,
  proofErrorText,
  proofSkipped,
  sessionBody,
  supportEnterUrl,
} from "@/utils/supportAccess";
import { SupportAdminApi, SupportProofApi } from "@/api/standards";
import OtpStep from "./OtpStep.vue";

// 화면 env(빌드 때 값이 박힘) — 목록은 supportAccess.js 한 곳
const APP_ENV = appUrlEnv();
/** 실패 문구: 본인 확인을 스스로 닫았으면 없음, 새 계약 코드는 공통 문구, 아니면 서버 문구 */
const failText = (error, fallback) => {
  if (isStepUpCancelled(error)) return "";
  const n = standardErrorNotice(error);
  return n ? n.text : errorMessage(error, fallback);
};

/**
 * POST /support/sessions → 새 탭. 본인 확인 기록이 없으면(403 SUPPORT_PROOF_REQUIRED) 같은 창에서 확인 뒤 한 번만 다시.
 * 앱·사유는 기본값이 골라져 있어 바로 「접속」(앱이 하나면 고르는 칸도 없음).
 */
export default {
  name: "SupportEntryDialog",
  components: { OtpStep },
  props: {
    open: { type: Boolean, default: false },
    companyId: { type: String, default: "" },
    companyType: { type: String, default: "" },
    companyName: { type: String, default: "" },
  },
  emits: ["update:open", "opened"],
  data() {
    return {
      APP_LABEL,
      ENTRY_HELP,
      PROOF_HINT,
      SUPPORT_REASONS,
      entry: {},
      step: "form",
      message: "",
      entryUrl: "",
      triedSaved: false,
      busy: false,
      proofFactors: { methods: [], targets: {} },
    };
  },
  computed: {
    apps() {
      return appsForCompanyType(this.companyType);
    },
  },
  watch: {
    open: {
      immediate: true,
      handler(v) {
        if (v) this.reset();
      },
    },
  },
  mounted() {
    // 부트스트랩 창 안에서 열려도 focus trap 이 입력 칸 포커스를 빼앗지 않게(StepUpModal 과 같은 방식)
    window.addEventListener("focusin", this.keepFocus, true);
  },
  beforeUnmount() {
    window.removeEventListener("focusin", this.keepFocus, true);
  },
  methods: {
    keepFocus(event) {
      if (this.open && event.target.closest && event.target.closest(".support-dialog")) event.stopPropagation();
    },
    reset() {
      this.entry = { app: this.apps[0], reasonCode: "CUSTOMER_REQUEST", reasonNote: "" };
      this.step = "form";
      this.message = "";
      this.entryUrl = "";
      this.triedSaved = false;
    },
    close() {
      if (this.busy) return;
      this.$emit("update:open", false);
    },
    async start(proofDone) {
      const problem = entryError(this.entry, this.companyType);
      if (problem) {
        this.message = problem;
        return;
      }
      this.busy = true;
      this.message = "";
      try {
        const data = await SupportAdminApi.startSession(sessionBody(this.companyId, this.entry));
        const url = supportEnterUrl(data.app || this.entry.app, data.code, APP_ENV);
        if (!url) {
          this.message = "앱 주소 설정이 없어 새 탭을 열 수 없습니다. 운영팀에 알려 주세요.";
          return;
        }
        // noopener 를 창 기능으로 주면 열려도 null 이라 막힘을 알 수 없다 → 연 뒤 opener 를 끊는다
        const win = window.open(url, "_blank");
        if (win) win.opener = null;
        this.entryUrl = win ? "" : url;
        this.step = "opened";
        this.$emit("opened"); // 첫 접속이면 서브 계정이 생김 — 카드가 다시 읽음
      } catch (error) {
        console.error(error);
        const next = entryNextStep(error, proofDone);
        if (next && next.kind === "PROOF") await this.startProof(); // 수단을 읽는 동안 버튼은 바쁨
        else this.message = next ? next.text : failText(error, "접속하지 못했습니다. 잠시 뒤 다시 시도하세요.");
      } finally {
        this.busy = false;
      }
    },
    async startProof() {
      const mfa = await SupportProofApi.factors().catch((error) => {
        console.error(error);
        return null;
      });
      // 2차 인증을 쓰지 않는 사람은 본인 확인 없이 같은 요청을 한 번만 다시(T-CONTRACT-1031 §1). 또 막히면 entryNextStep 이 문구로
      if (proofSkipped(mfa)) {
        await this.start(true);
        return;
      }
      // 방금(10분 안) 받은 본인 확인 증표가 있으면 인증번호를 다시 보내지 않고 그대로 씀(10-29). 지났으면 서버가 거절 → 인증번호로
      const saved = getMfaToken();
      if (saved && !this.triedSaved) {
        this.triedSaved = true;
        try {
          await SupportProofApi.proof(proofBody(saved));
          await this.start(true);
          return;
        } catch (error) {
          console.error(error);
        }
      }
      this.step = "proof";
      // 수단을 못 읽었으면 알림톡으로 시도(서버가 답함)
      this.proofFactors = mfa ? factorsOf(mfa) : { methods: ["ALIMTALK_OTP"], targets: {} };
      if (!this.proofFactors.methods.length) this.message = PROOF_NEEDS_FACTOR_TEXT;
    },
    async proofSend(method) {
      const data = await SupportProofApi.challenge(PROOF_PURPOSE, method);
      // 2FA 예외면 인증번호 없이 증표가 오지만 /support/proof 가 거절한다(계약) → 그대로 보내 서버 답을 보인다
      if (data && data.mfaToken) await this.finishProof(data.mfaToken);
      return data;
    },
    async proofVerify(method, code) {
      const data = await SupportProofApi.verify(PROOF_PURPOSE, method, code);
      setMfaToken(data.mfaToken);
      await this.finishProof(data.mfaToken);
    },
    async finishProof(mfaToken) {
      try {
        await SupportProofApi.proof(proofBody(mfaToken));
      } catch (error) {
        console.error(error);
        this.message = proofErrorText(error) || failText(error, "본인 확인을 마치지 못했습니다.");
        return;
      }
      this.step = "form";
      // 사용자가 다시 누르지 않게 막혔던 같은 요청을 저절로 다시 보냄
      await this.start(true);
    },
  },
};
</script>

<style scoped>
.field-label {
  display: block;
  margin: 14px 0 6px;
  font-size: 13px;
  font-weight: 600;
  color: #2f3337;
}
.field-hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: #5c6168;
}
.entry-error {
  margin: 10px 0 0;
  font-size: 13px;
  color: #b71c1c;
}
/* 창 본문 여백(레포 규칙 src/modal.mdc — 전역 .modal-content 테두리·.ant-modal-content padding 0 을 덮음, FIX-1101 12) */
.modal-content {
  padding: 20px;
  border: none;
}
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}
.modal-footer :deep(.yellow-button) {
  background-color: #ffcc32 !important;
  border-color: #ffcc32 !important;
  color: #000 !important;
}
</style>
